import React, { useState } from "react";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormikProps } from "formik";
import UserStatus from "enums/UserStatus";
import {
  RemoveUserModal,
  ResendUserInviteModal,
  ResetUserPasswordModal,
} from "../../common/users/UserModules";
import { FormikAddUser } from "hooks/useUserProfileData";
import Role from "enums/Role";
import dayjs from "utils/dayjs";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import Summary, { SummaryItem } from "../../common/Summary";
import { Inline } from "components/layout/reusableLayouts";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { formatAddressDisplay } from "hooks/useAddressData";
import { fullName } from "utils/fullName";

interface DisplayUserInformationProps {
  handleClickEdit: () => void;
  formik: FormikProps<FormikAddUser>;
  /**
   * Lead with name and email. The profile drawer carries both in its identity
   * block above this, but the add-user review step has no such block, so
   * without this the three required fields the user just typed are the only
   * ones missing from the summary of them.
   */
  includeIdentity?: boolean;
  handleUpdateUser?: (error?: string) => void;
  handleClickReactivateUser?: () => void;
  /** Your own record: you may edit it, but nobody resets, deactivates or reinvites themselves here. */
  ownRecord?: boolean;
}

const DisplayUserInformation: React.FC<DisplayUserInformationProps> = (
  props,
) => {
  const {
    handleClickEdit,
    handleUpdateUser,
    handleClickReactivateUser,
    formik,
    includeIdentity = false,
    ownRecord = false,
  } = props;

  const { t } = useTranslation();

  const [openResetUserPasswordModal, setOpenResetUserPasswordModal] =
    useState(false);
  const [openRemoveUserModal, setOpenRemoveUserModal] = useState(false);
  const [openResendUserInviteModal, setOpenResendUserInviteModal] =
    useState(false);

  const handleclickResetPassword = () => {
    setOpenResetUserPasswordModal(true);
  };

  const handleClickRemoveUser = () => {
    setOpenRemoveUserModal(true);
  };

  const handleClickResendInvite = () => {
    setOpenResendUserInviteModal(true);
  };

  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const canManage = isAdminOrHigher && !ownRecord;
  // Cardholders have no Frontegg account — none of these auth actions apply to them.
  const isCardOnlyUser = formik.values.role === Role.Cardholder;

  const { filteredCustomFieldSummaryItems } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.USER,
    customFields: formik.values.customFields,
  });

  // Identity leads when it is asked for, because pairing here is positional
  // and empty values are dropped: name and email are required, so they can
  // never re-column the rows below them the way an absent Business Location
  // would. The profile drawer omits them, having its own identity block.
  const name = fullName(formik.values);

  const summaryItems: SummaryItem[] = [
    ...(includeIdentity
      ? [
          {
            title: t("Name"),
            value: name,
          },
          {
            title: t("Email Address"),
            value: formik.values?.email,
          },
        ]
      : []),
    {
      title: t("Phone Number"),
      value: formik.values?.phone,
    },
    {
      title: t("Role"),
      // Translated, as the table's role chip is: without this the same user
      // reads "Admin" here and "Administrador" one screen away.
      value: formik.values?.role ? t(formik.values.role) : null,
    },
    {
      title: t("Date of Birth"),
      value: formik.values?.birthdate
        ? dayjs(formik.values.birthdate).format("MMMM D")
        : null,
    },
    {
      title: t("Business Location"),
      value: formik.values?.location?.addressName,
    },
    {
      title: t("Address"),
      fullWidth: true,
      value: formatAddressDisplay(formik.values.address),
    },
    {
      title: t("Manager"),
      value: fullName(formik.values?.assignedManager) || null,
    },
    ...filteredCustomFieldSummaryItems,
  ];

  return (
    <>
      <Summary
        items={summaryItems}
        alignEndColumn
        disableContainer
        children={
          (isAdminOrHigher || ownRecord) &&
          formik.values.id && (
            <Inline gap="var(--space-3)" justify="center">
              {formik.values.status !== UserStatus.INACTIVE && (
                <Button
                  variant="default-outlined"
                  size="small"
                  onClick={handleClickEdit}
                >
                  {t("Edit User Info")}
                </Button>
              )}

              {canManage &&
                !isCardOnlyUser &&
                formik.values.status !== UserStatus.PENDING && (
                  <Button
                    variant="default-soft"
                    size="small"
                    onClick={handleclickResetPassword}
                  >
                    {t("Reset Password")}
                  </Button>
                )}

              {canManage &&
                !isCardOnlyUser &&
                formik.values.status === UserStatus.PENDING && (
                  <Button
                    variant="secondary-filled"
                    size="small"
                    onClick={handleClickResendInvite}
                  >
                    {t("Resend Invite")}
                  </Button>
                )}

              {canManage && formik.values.status !== UserStatus.INACTIVE && (
                <Button
                  variant="danger-filled"
                  size="small"
                  onClick={handleClickRemoveUser}
                >
                  {t("Deactivate")}
                </Button>
              )}

              {canManage &&
                formik.values.status === UserStatus.INACTIVE &&
                handleClickReactivateUser && (
                  <Button
                    variant="primary-filled"
                    size="small"
                    onClick={() =>
                      formik.values.id && handleClickReactivateUser()
                    }
                  >
                    {t("Reactivate")}
                  </Button>
                )}
            </Inline>
          )
        }
      />

      {formik.values?.id && (
        <RemoveUserModal
          openRemoveUserModal={openRemoveUserModal}
          setOpenRemoveUserModal={setOpenRemoveUserModal}
          userId={formik.values.id}
          userEmail={formik.values?.email}
          handleUpdateUser={handleUpdateUser}
        />
      )}

      <ResetUserPasswordModal
        openResetUserPasswordModal={openResetUserPasswordModal}
        setOpenResetUserPasswordModal={setOpenResetUserPasswordModal}
        userEmail={formik.values?.email}
      />

      <ResendUserInviteModal
        openResendUserInviteModal={openResendUserInviteModal}
        setOpenResendUserInviteModal={setOpenResendUserInviteModal}
        userEmail={formik.values?.email}
        handleUpdateUser={handleUpdateUser}
      />
    </>
  );
};

export default DisplayUserInformation;
